import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, Loader2, PackageCheck, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";

function amountLabel(line) {
  if (line.counting_method === "estimate") return "treatment uses";
  const unit = String(line.stock_unit || "unit").trim();
  return unit.endsWith("s") ? unit : `${unit}s`;
}

function normalizeLines(lines) {
  return (Array.isArray(lines) ? lines : []).map((line) => ({
    ...line,
    amount_used: String(line.amount_used ?? 1),
  }));
}

export default function CheckoutMaterials({ appointmentId, items, readOnly = false, tutorialDemo = false }) {
  const requestItems = useMemo(() => (Array.isArray(items) ? items : [])
    .filter((item) => item?.procedure_id && (item.outcome || "done") === "done")
    .map((item) => ({
      procedure_id: item.procedure_id,
      procedure_visit_id: item.procedure_visit_id || null,
      outcome: item.outcome || "done",
    })), [items]);
  const requestSignature = useMemo(() => JSON.stringify(requestItems), [requestItems]);
  const [model, setModel] = useState(null);
  const [lines, setLines] = useState([]);
  const [selectedSupplyId, setSelectedSupplyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);

  const loadMaterials = useCallback(async () => {
    if (!appointmentId || tutorialDemo) {
      setLoading(false);
      return;
    }
    setExpanded(false);
    setLoading(true);
    setError("");
    const { data, error: rpcError } = await supabase.rpc("inventory_checkout_materials_v2", {
      p_appointment_id: appointmentId,
      p_items: JSON.parse(requestSignature),
    });
    setLoading(false);
    if (rpcError) {
      setError(rpcError.message || "Materials could not be loaded.");
      return;
    }
    setModel(data || {});
    setLines(normalizeLines(data?.lines));
    setDirty(false);
  }, [appointmentId, requestSignature, tutorialDemo]);

  useEffect(() => { loadMaterials(); }, [loadMaterials]);

  const supplies = Array.isArray(model?.supplies) ? model.supplies : [];
  const visitKit = Array.isArray(model?.visit_kit) ? model.visit_kit : [];
  const availableSupplies = supplies.filter((supply) => (
    !supply.automatic_visit_kit
    && !lines.some((line) => line.supply_id === supply.supply_id)
  ));
  const locked = readOnly || model?.consumed;

  useEffect(() => {
    if (!availableSupplies.some((supply) => supply.supply_id === selectedSupplyId)) {
      setSelectedSupplyId(availableSupplies[0]?.supply_id || "");
    }
  }, [availableSupplies, selectedSupplyId]);

  function updateAmount(supplyId, value) {
    setLines((current) => current.map((line) => (
      line.supply_id === supplyId ? { ...line, amount_used: value } : line
    )));
    setDirty(true);
    setError("");
  }

  function removeLine(supplyId) {
    setLines((current) => current.filter((line) => line.supply_id !== supplyId));
    setDirty(true);
    setError("");
  }

  function addLine() {
    const supply = supplies.find((item) => item.supply_id === selectedSupplyId);
    if (!supply) return;
    setLines((current) => [...current, {
      line_id: null,
      supply_id: supply.supply_id,
      name: supply.name,
      stock_unit: supply.stock_unit,
      counting_method: supply.counting_method,
      uses_per_stock_unit: supply.uses_per_stock_unit,
      amount_used: "1",
      source: "added",
    }]);
    setDirty(true);
    setError("");
  }

  async function saveMaterials() {
    const invalid = lines.some((line) => !Number.isFinite(Number(line.amount_used)) || Number(line.amount_used) <= 0);
    if (invalid) {
      setError("Every material needs an amount greater than zero.");
      return;
    }
    setSaving(true);
    setError("");
    const { data, error: rpcError } = await supabase.rpc("inventory_checkout_set_materials_v2", {
      p_appointment_id: appointmentId,
      p_lines: lines.map((line) => ({
        supply_id: line.supply_id,
        amount_used: Number(line.amount_used),
      })),
    });
    setSaving(false);
    if (rpcError) {
      setError(rpcError.message || "Materials could not be saved.");
      return;
    }
    setModel(data || {});
    setLines(normalizeLines(data?.lines));
    setDirty(false);
    setExpanded(false);
    toast.success("Materials saved for this checkout");
  }

  if (tutorialDemo || !appointmentId) return null;

  const materialCount = lines.length;
  const materialWord = materialCount === 1 ? "material" : "materials";
  const visitKitSummary = visitKit.length > 0 ? ` ${visitKit.length} standard visit ${visitKit.length === 1 ? "supply is" : "supplies are"} automatic.` : "";
  let summary = `${materialCount} suggested treatment ${materialWord}.${visitKitSummary} Review only if today was different.`;
  let summaryClassName = "text-slate-500";
  if (loading) {
    summary = "Checking treatment recipes";
  } else if (error && !model) {
    summary = "Materials unavailable. Checkout will use saved treatment recipes.";
    summaryClassName = "text-amber-700";
  } else if (dirty) {
    summary = "Unsaved material changes";
    summaryClassName = "font-semibold text-amber-700";
  } else if (model?.consumed) {
    summary = `${materialCount} treatment ${materialWord} recorded.${visitKitSummary}`;
    summaryClassName = "font-medium text-emerald-700";
  } else if (model?.saved) {
    summary = `${materialCount} treatment ${materialWord} saved.${visitKitSummary}`;
    summaryClassName = "font-medium text-emerald-700";
  } else if (materialCount === 0) {
    summary = visitKit.length > 0
      ? `${visitKit.length} standard visit ${visitKit.length === 1 ? "supply is" : "supplies are"} recorded automatically.`
      : "No materials suggested. Add only if something was used.";
  }

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="checkout-materials">
      <button
        type="button"
        className="flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left outline-none transition-colors hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-600 sm:px-5"
        aria-expanded={expanded}
        aria-controls="checkout-materials-details"
        data-testid="checkout-materials-toggle"
        onClick={() => setExpanded((current) => !current)}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600"><PackageCheck className="h-4 w-4" aria-hidden="true" /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-slate-900">Materials used this session</span>
          <span className={`mt-0.5 block text-xs leading-5 ${summaryClassName}`}>{summary}</span>
        </span>
        <span className="inline-flex min-h-11 shrink-0 items-center gap-1.5 text-xs font-semibold text-slate-600">
          {expanded ? "Close" : "Review"}
          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
        </span>
      </button>

      {expanded ? (
        <div id="checkout-materials-details">
          {loading ? (
            <div className="flex items-center gap-2 border-t border-slate-100 px-5 py-5 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading materials</div>
          ) : error && !model ? (
            <div className="border-t border-slate-100 px-5 py-4">
              <p className="flex items-start gap-2 text-sm text-amber-800"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{error} Checkout can continue using the saved treatment recipes.</p>
              <button type="button" onClick={loadMaterials} className="mt-3 min-h-11 rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-semibold text-slate-700">Try again</button>
            </div>
          ) : (
            <>
              {visitKit.length > 0 ? (
                <div className="border-t border-slate-100 bg-slate-50 px-4 py-4 sm:px-5" data-testid="checkout-patient-visit-kit">
                  <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">Standard patient visit kit</p>
                  <p className="mt-1 text-sm text-slate-700">{visitKit.map((item) => item.name).join(", ")}</p>
                  <p className="mt-1 text-xs leading-5 text-slate-500">Recorded once when this patient visit is completed. These supplies are managed in Inventory and are not added again here.</p>
                </div>
              ) : null}
              <div className="divide-y divide-slate-100 border-t border-slate-100">
                {lines.length === 0 ? (
                  <div className="px-4 py-4 text-sm text-slate-500 sm:px-5">No tracked materials selected for this session.</div>
                ) : lines.map((line) => (
                  <div key={line.supply_id} className="grid gap-3 px-4 py-3.5 sm:grid-cols-[minmax(180px,1fr)_180px_44px] sm:items-end sm:px-5">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{line.name}</p>
                      <p className="mt-0.5 text-xs text-slate-500">{line.counting_method === "estimate" ? `1 ${line.stock_unit} lasts about ${Number(line.uses_per_stock_unit || 1).toLocaleString()} treatments` : `Counted per ${line.stock_unit}`}</p>
                    </div>
                    <label className="text-xs font-semibold text-slate-600">
                      Amount used ({amountLabel(line)})
                      <input type="number" min="0.01" step="0.01" value={line.amount_used} onChange={(event) => updateAmount(line.supply_id, event.target.value)} disabled={locked} aria-label={`${line.name} amount used`} className="mt-1 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-right text-base font-semibold tabular-nums text-slate-900 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 disabled:bg-slate-50 disabled:text-slate-500 sm:text-sm" />
                    </label>
                    {!locked ? <button type="button" onClick={() => removeLine(line.supply_id)} aria-label={`Remove ${line.name}`} className="flex h-11 w-full items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 hover:border-rose-300 hover:text-rose-700 sm:w-11"><Trash2 className="h-4 w-4" /></button> : <span />}
                  </div>
                ))}
              </div>

              {!locked && availableSupplies.length > 0 ? (
                <div className="flex flex-col gap-2 border-t border-slate-100 bg-slate-50 px-4 py-3 sm:flex-row sm:items-end sm:px-5">
                  <label className="min-w-0 flex-1 text-xs font-semibold text-slate-600">Add another material<select value={selectedSupplyId} onChange={(event) => setSelectedSupplyId(event.target.value)} className="mt-1 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100">{availableSupplies.map((supply) => <option key={supply.supply_id} value={supply.supply_id}>{supply.name}</option>)}</select></label>
                  <button type="button" onClick={addLine} className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"><Plus className="h-4 w-4" />Add</button>
                </div>
              ) : null}

              {error ? <p role="alert" className="border-t border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-800 sm:px-5">{error}</p> : null}
              {!locked && dirty ? (
                <div className="flex flex-col gap-2 border-t border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                  <p className="text-xs text-slate-500">Save changes so Dental OS records this list when checkout is completed.</p>
                  <button type="button" disabled={saving} onClick={saveMaterials} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Save materials</button>
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}
